import { REWARD_ITEMS, type RewardItem, type RewardSlot, type RewardSource } from '@shkola/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';

export {
  REWARD_ITEMS,
  REWARD_POINTS,
  type RewardItem,
  type RewardSlot,
  type RewardSource,
  rewardItem,
} from '@shkola/core';

/**
 * «Магазин нагород»: sparks for effort (counted by the database from what the student did) spent
 * on a frame for the animal, the sky of the home page and a buddy sticker. A grade cannot be bought,
 * there is no ranking: the student and the family see the sparks, nobody else.
 */
export interface RewardWallet {
  earned: number;
  spent: number;
  balance: number;
  owned: string[];
  /** what is worn now, by slot */
  worn: Record<RewardSlot, string | null>;
}

export interface RewardDeed {
  at: string;
  source: RewardSource;
  points: number;
  /** the homework, the subject or the station */
  title: string | null;
}

const NOTHING: RewardWallet['worn'] = { frame: null, sky: null, buddy: null };

export function useRewardWallet(studentId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['rewards', 'wallet', studentId],
    enabled: studentId !== '',
    queryFn: async (): Promise<RewardWallet> => {
      const [balance, owned, profile] = await Promise.all([
        db.rpc('reward_balance', { p_student: studentId }),
        db.from('reward_items').select('item').eq('student_member_id', studentId),
        db
          .from('reward_profiles')
          .select('frame, sky, buddy')
          .eq('student_member_id', studentId)
          .maybeSingle(),
      ]);
      const sum = must(balance)[0] ?? { earned: 0, spent: 0, balance: 0 };
      if (profile.error) throw profile.error;
      return {
        earned: sum.earned,
        spent: sum.spent,
        balance: sum.balance,
        owned: must(owned).map((o) => o.item),
        worn: profile.data
          ? { frame: profile.data.frame, sky: profile.data.sky, buddy: profile.data.buddy }
          : NOTHING,
      };
    },
  });
}

export function useRewardHistory(studentId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['rewards', 'history', studentId],
    enabled: studentId !== '',
    queryFn: async (): Promise<RewardDeed[]> =>
      must(await db.rpc('reward_history', { p_student: studentId, p_limit: 40 })).map((d) => ({
        at: d.at,
        source: d.source as RewardSource,
        points: d.points,
        title: d.title,
      })),
  });
}

export function useRewardActions(studentId: string) {
  const db = useDb();
  const client = useQueryClient();
  const onSettled = () => void client.invalidateQueries({ queryKey: ['rewards'] });
  return {
    buy: useMutation({
      mutationFn: async (item: string) =>
        must(await db.rpc('buy_reward', { p_student: studentId, p_item: item })),
      onSettled,
    }),
    wear: useMutation({
      mutationFn: async (a: { slot: RewardSlot; item: string | null }) =>
        must(
          await db.rpc('wear_reward', {
            p_student: studentId,
            p_slot: a.slot,
            ...(a.item && { p_item: a.item }),
          }),
        ),
      onSettled,
    }),
  };
}

/** The catalog by slot, cheapest first. */
export function rewardShelves(): Array<{ slot: RewardSlot; items: RewardItem[] }> {
  return (['frame', 'sky', 'buddy'] as const).map((slot) => ({
    slot,
    items: REWARD_ITEMS.filter((i) => i.slot === slot).sort((a, b) => a.price - b.price),
  }));
}

/** How many more sparks are needed for an item (0 — enough). */
export const sparksShort = (balance: number, item: Pick<RewardItem, 'price'>) =>
  Math.max(0, item.price - balance);
